import React, { useEffect, useState } from 'react';
import StandardSpinner from '../../utils/Spinner';
import { useTheme } from '../../Context/themeContext';

const TermsAndPrivacy = (tab) => {
  const { theme } = useTheme();
  const [loading, setloading] = useState(true);
  const [activeTab, setActiveTab] = useState('terms');

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark 
    ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900'
    : 'bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50';
  
  const bgTabContainer = isDark ? 'bg-gray-800/50' : 'bg-gray-200/50';
  const bgTabActive = isDark ? 'bg-blue-600' : 'bg-blue-500';
  const bgTabInactive = isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-300';
  const bgCard = isDark ? 'bg-gray-800/50' : 'bg-white/80';
  const bgCardHeader = isDark ? 'bg-gray-900/80' : 'bg-gray-100/80';
  const bgSectionHover = isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-100/50';
  const bgBadge = isDark ? 'bg-blue-600/20 group-hover:bg-blue-600/30' : 'bg-blue-100 group-hover:bg-blue-200';
  
  const borderColor = isDark ? 'border-blue-900/50' : 'border-gray-200';
  const borderLight = isDark ? 'border-blue-800/50' : 'border-gray-200';
  const borderDivider = isDark ? 'border-blue-900/30' : 'border-gray-200';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-300' : 'text-gray-700';
  //const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
  const textBlue = isDark ? 'text-blue-300' : 'text-blue-600';
  //const textBlueLight = isDark ? 'text-blue-400' : 'text-blue-600';
  const textTabInactive = isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  const textBadge = isDark ? 'text-blue-400' : 'text-blue-600';
  
  const shadowColor = isDark ? 'shadow-blue-600/30' : 'shadow-blue-500/20';
  const shadowLight = isDark ? 'shadow-lg' : 'shadow-md';
  
  const orbColor1 = isDark ? 'bg-blue-600/10' : 'bg-blue-400/20';
  const orbColor2 = isDark ? 'bg-blue-800/10' : 'bg-blue-300/20';

  useEffect(() => {
    setTimeout(() => {
      setloading(false);
    }, 800);
  }, []);

  const termsData = {
    lastUpdated: "March 9, 2026",
    sections: [
      {
        title: "Acceptance of Terms",
        content: "By accessing and using this service, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by these terms, please do not use this service."
      },
      {
        title: "Description of Service",
        content: "We provide users with access to a rich collection of resources, including various tools, educational materials, and customizable content. Unless explicitly stated otherwise, any new features that augment or enhance the current service shall be subject to these Terms of Service."
      },
      {
        title: "User Responsibilities",
        content: "You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account. You should notify us immediately of any unauthorized use of your account."
      },
      {
        title: "Privacy and Data Protection",
        content: "Your privacy is important to us. We handle your data according to our Privacy Policy. By using our service, you consent to such processing and warrant that all data provided by you is accurate."
      },
      {
        title: "Intellectual Property Rights",
        content: "The service and its original content, features, and functionality are and will remain the exclusive property of our company and its licensors. The service is protected by copyright, trademark, and other laws."
      }
    ]
  };

  const privacyData = {
    lastUpdated: "March 9, 2026",
    sections: [
      {
        title: "Information We Collect",
        content: "We collect information you provide directly to us, such as when you create an account, use our services, or communicate with us. This may include your name, email address, phone number, and payment information."
      },
      {
        title: "How We Use Your Information",
        content: "We use the information we collect to provide, maintain, and improve our services, to process transactions, to send you technical notices and support messages, and to respond to your comments and questions."
      },
      {
        title: "Information Sharing and Disclosure",
        content: "We do not share your personal information with third parties except as described in this policy. We may share information with vendors, consultants, and other service providers who need access to such information to carry out work on our behalf."
      },
      {
        title: "Data Security",
        content: "We take reasonable measures to help protect your personal information from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction. However, no security system is impenetrable."
      },
      {
        title: "Your Rights and Choices",
        content: "You may update, correct, or delete your account information at any time by logging into your account. You may also contact us to request access to, change, or delete your personal information."
      }
    ]
  };

  if (loading) {
    return <StandardSpinner />;
  }

  return (
    <div className={`min-h-screen ${bgPrimary} transition-colors duration-300`}>
      {/* Tab Navigation */}
      <div className="max-w-4xl px-4 py-24 mx-auto">
        <div className={`flex p-1 mb-8 space-x-2 rounded-lg ${bgTabContainer} w-fit transition-colors duration-300`}>
          <button
            onClick={() => setActiveTab('terms')}
            className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
              activeTab === 'terms'
                ? `${bgTabActive} text-white ${shadowLight} ${shadowColor}`
                : `${textTabInactive} ${bgTabInactive}`
            }`}
          >
            Terms of Service
          </button>
          <button
            onClick={() => setActiveTab('privacy')}
            className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
              activeTab === 'privacy'
                ? `${bgTabActive} text-white ${shadowLight} ${shadowColor}`
                : `${textTabInactive} ${bgTabInactive}`
            }`}
          >
            Privacy Policy
          </button>
        </div>

        {/* Content Area */}
        <div className={`overflow-hidden border ${bgCard} backdrop-blur-sm rounded-xl ${borderColor} transition-colors duration-300`}>
          {/* Last Updated Badge */}
          <div className={`px-6 py-3 border-b ${bgCardHeader} ${borderLight} transition-colors duration-300`}>
            <p className={`text-sm ${textBlue}`}>
              Last Updated: {activeTab === 'terms' ? termsData.lastUpdated : privacyData.lastUpdated}
            </p>
          </div>

          {/* Sections */}
          <div className="p-6 space-y-6">
            {(activeTab === 'terms' ? termsData.sections : privacyData.sections).map((section, index) => (
              <div 
                key={index}
                className={`p-4 transition-all duration-200 rounded-lg ${bgSectionHover}`}
              >
                <h2 className={`flex items-center mb-3 text-xl font-semibold ${textPrimary}`}>
                  <span className={`flex items-center justify-center w-8 h-8 mr-3 transition-colors rounded-lg ${bgBadge}`}>
                    <span className={`font-bold ${textBadge}`}>{index + 1}</span>
                  </span>
                  {section.title}
                </h2>
                <p className={`leading-relaxed ${textSecondary} pl-11`}>
                  {section.content}
                </p>
                {index < (activeTab === 'terms' ? termsData.sections.length - 1 : privacyData.sections.length - 1) && (
                  <div className={`mt-4 border-b ${borderDivider}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Background Decorative Elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-[-1]">
        <div className={`absolute rounded-full top-20 left-10 w-72 h-72 ${orbColor1} blur-3xl`}></div>
        <div className={`absolute rounded-full bottom-20 right-10 w-96 h-96 ${orbColor2} blur-3xl`}></div>
      </div>
    </div>
  );
};

export default TermsAndPrivacy;
